import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useDemo } from "@/store/DemoStore";
import { api, errMsg } from "@/lib/api";
import { Card, Logo, btn } from "@/components/common";
import { cn } from "@/lib/utils";

const Spinner = () => <div className="app-shell grid place-items-center bg-[#F5F7FA]"><Loader2 className="h-6 w-6 animate-spin text-navy-700" /></div>;

const field = "h-11 w-full rounded-xl border border-slate-200 bg-white px-3.5 text-base text-navy-900 outline-none focus:border-navy-500 focus:ring-2 focus:ring-navy-100 sm:text-sm";

function ChangePassword() {
  const { refresh, logout } = useDemo();
  const [f, setF] = useState({ current: "", new: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (f.new !== f.confirm) return toast.error("New passwords don't match");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current: f.current, new: f.new });
      toast.success("Password updated");
      await refresh();
    } catch (err) {
      toast.error(errMsg(err));
    }
    setBusy(false);
  };
  return (
    <div className="app-shell scroll-area grid place-items-center bg-[#F5F7FA] px-4">
      <Card className="w-full max-w-md p-6 sm:p-8" data-testid="change-password-screen">
        <Logo />
        <h1 className="mt-6 font-display text-2xl font-bold text-navy-900">Set your password</h1>
        <p className="mt-1 text-sm text-slate-500">Replace your temporary password before continuing. Use at least 8 characters.</p>
        <form onSubmit={submit} className="mt-6 space-y-3">
          {[["current", "Temporary password"], ["new", "New password"], ["confirm", "Confirm new password"]].map(([k, l]) => (
            <label key={k} className="block"><span className="mb-1.5 block text-xs font-medium text-slate-500">{l}</span>
              <input type="password" required minLength={k === "current" ? 1 : 8} className={field} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`change-password-${k}`} /></label>
          ))}
          <button disabled={busy} className={`${btn.primary} w-full`} data-testid="change-password-submit">Save Password</button>
          <button type="button" onClick={logout} className={`${btn.ghost} w-full`}>Sign Out</button>
        </form>
      </Card>
    </div>
  );
}

function AgreementScreen() {
  const { refresh, logout } = useDemo();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [name, setName] = useState("");
  const [plan, setPlan] = useState("");
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/agreement").then(({ data }) => setDoc(data)).catch(() => toast.error("Could not load the agreement")); }, []);
  const ready = name.trim().length >= 2 && !!plan && ack;
  const submit = async (e) => {
    e.preventDefault();
    if (!ready) return;
    setBusy(true);
    try {
      await api.post("/agreement/sign", { fullName: name.trim(), plan, acknowledged: ack });
      toast.success("Agreement signed");
      await refresh();
    } catch (err) { toast.error(errMsg(err)); setBusy(false); }
  };
  const signOut = async () => { await logout(); navigate("/portal"); };
  return (
    <div className="app-shell scroll-area flex flex-col bg-[#F5F7FA]" data-testid="agreement-screen">
      <div className="mx-auto flex w-full max-w-3xl flex-1 flex-col px-4 py-6 sm:py-10">
        <Logo />
        <h1 className="mt-5 font-display text-2xl font-bold text-navy-900 sm:text-3xl">{doc?.title || "Travel Agent Agreement"}</h1>
        <p className="mt-1 text-sm text-slate-500">Please review and sign this agreement to begin using Compass. You can't continue until it's signed.</p>
        {!doc ? <div className="grid flex-1 place-items-center py-16"><Loader2 className="h-6 w-6 animate-spin text-navy-700" /></div> : (
          <form onSubmit={submit} className="mt-5 space-y-5">
            <div className="max-h-[48vh] overflow-y-auto whitespace-pre-wrap rounded-2xl border border-slate-200 bg-white p-5 text-sm leading-relaxed text-navy-800 sm:p-6" data-testid="agreement-text">{doc.text}</div>
            <Card className="space-y-4 p-5 sm:p-6">
              <div>
                <p className="mb-2 text-sm font-semibold text-navy-900">Select your commission plan <span className="text-red-500">*</span></p>
                <div className="space-y-2">
                  {Object.entries(doc.plans || {}).map(([k, label]) => (
                    <label key={k} className={cn("flex cursor-pointer items-start gap-3 rounded-xl border p-3 text-sm transition-colors", plan === k ? "border-navy-900 bg-navy-50/50" : "border-slate-200 hover:border-navy-300")} data-testid={`agreement-plan-${k}`}>
                      <input type="radio" name="plan" value={k} checked={plan === k} onChange={() => setPlan(k)} className="mt-0.5 h-4 w-4 accent-navy-900" />
                      <span className="text-navy-800">{label}</span>
                    </label>
                  ))}
                </div>
              </div>
              <label className="block">
                <span className="mb-1.5 block text-xs font-medium text-slate-500">Type your full legal name to sign <span className="text-red-500">*</span></span>
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Jordan A. Rivera" autoComplete="name" className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3.5 text-base text-navy-900 outline-none focus:border-navy-500 focus:ring-2 focus:ring-navy-100 sm:text-sm" data-testid="agreement-name-input" />
              </label>
              <label className="flex cursor-pointer items-start gap-3 text-sm text-navy-800">
                <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-0.5 h-4 w-4 accent-navy-900" data-testid="agreement-ack-checkbox" />
                <span>I have read and agree to the {doc.title}, and I understand the commission plan and fees I selected above. I intend my typed name to serve as my electronic signature.</span>
              </label>
            </Card>
            <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
              <button type="button" onClick={signOut} className={btn.secondary} data-testid="agreement-signout-btn">Sign Out</button>
              <button disabled={!ready || busy} className={btn.primary} data-testid="agreement-sign-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign & continue"}</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export function RequireAuth({ kind, children }) {
  const { status, me } = useDemo();
  if (status === "loading") return <Spinner />;
  if (status === "mustChange") return kind === "agent" ? <ChangePassword /> : <Navigate to="/portal" replace />;
  if (status === "agreement") return kind === "agent" ? <AgreementScreen /> : <Navigate to="/portal" replace />;
  if (status !== "ready" || me?.kind !== kind) return <Navigate to="/portal" replace />;
  return children;
}
